/**
 * The phone's docked keypad, its pure half (PROD-1511): the record wizard's
 * review and the chef's Temperature logs both type through it.
 *
 * A tap on a tile opens the app's own count pad (the keys `CountInput` uses on
 * a tablet) docked at the bottom of the screen, editing that one value. Up and
 * down walk the tiles in PAPER order (Transport, Service, Temps, row by row),
 * so a whole row typed off the sheet never closes the pad.
 *
 * The first key after a tile opens REPLACES its value: the tile already shows a
 * number (a pre-fill, a photo's reading), and typing 39 over 50 must give 39,
 * not 5039. Backspace as the first key clears the tile.
 *
 * Pure — no React.
 */

import { USDA_HACCP_COLD_HOLD_MAX_F, USDA_HACCP_HOT_HOLD_MIN_F } from '@/api/production-records'
import type { TemperatureClass } from '@/components/cafeteria/temperature-table'
import type { RecordColumnKey } from '@/components/production/spreadsheet/record-spreadsheet-table'
import { applyDecimalKeypadKey, applyNumericKeypadKey, type NumericKeypadKey } from '@/lib/cafeteria/numeric-keypad'

/** The value being typed: the text the pad builds and whether the next key replaces it. */
export interface KeypadDraft {
  text: string
  /** True until the first key: that key replaces what the tile showed. */
  fresh: boolean
}

/** A tile's own value as the pad's starting text. */
export function tileText(value: number | '' | null): string {
  return value === '' || value === null ? '' : String(value)
}

/** The draft after one press. Done changes nothing (the caller closes the pad). */
export function pressKeypadKey(draft: KeypadDraft, key: NumericKeypadKey, decimal: boolean): KeypadDraft {
  if (key.kind === 'confirm') {
    return draft
  }
  if (draft.fresh && key.kind === 'backspace') {
    return { text: '', fresh: false }
  }
  const apply = decimal ? applyDecimalKeypadKey : applyNumericKeypadKey
  return { text: apply(draft.fresh ? '' : draft.text, key), fresh: false }
}

/** A temperature draft as the value it saves: empty is no reading, `38.` is 38. */
export function draftTemperature(text: string): number | null {
  if (text === '' || text === '.') {
    return null
  }
  const value = Number(text)
  return Number.isFinite(value) ? value : null
}

/** The paper's column order, which the pad's up / down follow. */
export const TILE_COLUMNS: readonly RecordColumnKey[] = [
  'ordered',
  'prepared',
  'received',
  'served',
  'reimbursable',
  'nonReimbursable',
  'leftover',
  'kitchenTemp',
  'receivedTemp',
  'servingTemp',
]

/** One tile: its row and its column (a record column, or the chef's `kitchen` / `dropOff`). */
export interface TileRef<C extends string = RecordColumnKey> {
  rowKey: string
  column: C
}

/** What `editableTiles` needs to know about a row's cells. */
export interface TileRowLike {
  key: string
  cells: Record<RecordColumnKey, { kind: 'count' | 'temp'; disabled?: boolean; offered?: boolean }>
}

/** Every tile that takes a value, in walking order: rows top to bottom, each in paper column order. */
export function editableTiles(rows: readonly TileRowLike[]): TileRef[] {
  const tiles: TileRef[] = []
  for (const row of rows) {
    for (const column of TILE_COLUMNS) {
      const cell = row.cells[column]
      if (!cell || cell.disabled || (cell.kind === 'temp' && cell.offered === false)) {
        continue
      }
      tiles.push({ rowKey: row.key, column })
    }
  }
  return tiles
}

/** The tile before or after `current`, or null at either end (or when `current` is not a tile). */
export function adjacentTile<C extends string>(
  tiles: readonly TileRef<C>[],
  current: TileRef<C>,
  direction: 1 | -1,
): TileRef<C> | null {
  const index = tiles.findIndex((tile) => tile.rowKey === current.rowKey && tile.column === current.column)
  if (index === -1) {
    return null
  }
  return tiles[index + direction] ?? null
}

/** The value a count is checked against, by column: Served against Received, a split against Served. */
const COMPARED_WITH: Partial<Record<RecordColumnKey, { column: RecordColumnKey; label: string }>> = {
  prepared: { column: 'ordered', label: 'Ordered' },
  received: { column: 'prepared', label: 'Prepared' },
  served: { column: 'received', label: 'Received' },
  reimbursable: { column: 'served', label: 'Served' },
  nonReimbursable: { column: 'served', label: 'Served' },
  leftover: { column: 'received', label: 'Received' },
}

/**
 * The keypad header's second line: what the value is checked against
 * (`Received 39` under Served), or for a reading its holding band. Null when
 * there is nothing to compare with.
 */
export function keypadHint(
  column: RecordColumnKey,
  valueOf: (column: RecordColumnKey) => number | '' | null,
  temperatureClass: TemperatureClass | null,
): string | null {
  if (column === 'kitchenTemp' || column === 'receivedTemp' || column === 'servingTemp') {
    if (temperatureClass === 'HOT') {
      return `Hot holding: ${USDA_HACCP_HOT_HOLD_MIN_F}°F or above`
    }
    if (temperatureClass === 'CHILLED') {
      return `Cold holding: ${USDA_HACCP_COLD_HOLD_MAX_F}°F or below`
    }
    return null
  }
  const compared = COMPARED_WITH[column]
  if (!compared) {
    return null
  }
  const value = valueOf(compared.column)
  return value === '' || value === null ? null : `${compared.label} ${value}`
}
